import { useCallback, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { fetchBanners, fetchGames } from '../api/catalog';
import { ApiError } from '../api/client';
import type { Banner, Game, LoadState } from '../api/types';
import type { GameCategory } from '../components/CategoryChips';

const PAGE_SIZE = 48;

function mapCategoryToType(_category: GameCategory): string | undefined {
  // Exact game_type match would drop "slot" / "slot-modern" rows; filter client-side instead.
  return undefined;
}

export function useCatalog() {
  const { t, i18n } = useTranslation('errors');
  const [banners, setBanners] = useState<Banner[]>([]);
  const [games, setGames] = useState<Game[]>([]);
  const [message, setMessage] = useState<string | null>(null);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [bannerState, setBannerState] = useState<LoadState>('idle');
  const [gameState, setGameState] = useState<LoadState>('idle');
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [category, setCategory] = useState<GameCategory>('all');
  const [providerCode, setProviderCode] = useState('');

  useEffect(() => {
    const timer = window.setTimeout(() => setDebouncedQuery(query.trim()), 300);
    return () => window.clearTimeout(timer);
  }, [query]);

  const loadBanners = useCallback(async () => {
    const locale = (i18n.resolvedLanguage || i18n.language || 'az').split('-')[0];
    // Drop previous-locale copy immediately so titles/CTA never linger mid-switch.
    setBanners([]);
    setBannerState('loading');
    try {
      const data = await fetchBanners({ locale });
      setBanners(data.banners);
      setBannerState('success');
    } catch {
      setBanners([]);
      setBannerState('success');
    }
  }, [i18n.language, i18n.resolvedLanguage]);

  const loadGames = useCallback(async () => {
    setGameState('loading');
    setError(null);
    setPage(1);
    try {
      const data = await fetchGames({
        q: debouncedQuery || undefined,
        game_type: mapCategoryToType(category),
        provider: providerCode || undefined,
        page: 1,
        per_page: PAGE_SIZE,
      });
      setGames(data.games);
      setTotal(data.total);
      setMessage(data.message);
      setPage(1);
      setGameState('success');
    } catch (err) {
      setGames([]);
      setTotal(0);
      setMessage(null);
      setGameState('error');
      if (err instanceof ApiError) {
        setError(t('generic'));
      } else {
        setError(t('network'));
      }
    }
  }, [category, debouncedQuery, providerCode, t]);

  const loadMore = useCallback(async () => {
    if (loadingMore || gameState === 'loading') return;
    if (games.length >= total) return;
    const nextPage = page + 1;
    setLoadingMore(true);
    setError(null);
    try {
      const data = await fetchGames({
        q: debouncedQuery || undefined,
        game_type: mapCategoryToType(category),
        provider: providerCode || undefined,
        page: nextPage,
        per_page: PAGE_SIZE,
      });
      setGames((prev) => {
        const seen = new Set(prev.map((g) => g.id));
        const merged = [...prev];
        for (const game of data.games) {
          if (!seen.has(game.id)) merged.push(game);
        }
        return merged;
      });
      setTotal(data.total);
      setMessage(data.message);
      setPage(nextPage);
      setGameState('success');
    } catch (err) {
      setGameState('error');
      if (err instanceof ApiError) {
        setError(t('generic'));
      } else {
        setError(t('network'));
      }
    } finally {
      setLoadingMore(false);
    }
  }, [
    category,
    debouncedQuery,
    gameState,
    games.length,
    loadingMore,
    page,
    providerCode,
    t,
    total,
  ]);

  useEffect(() => {
    void loadBanners();
  }, [loadBanners, i18n.language]);

  useEffect(() => {
    void loadGames();
  }, [loadGames, i18n.language]);

  const hasMore = games.length < total;

  return useMemo(
    () => ({
      banners,
      games,
      total,
      loadedCount: games.length,
      hasMore,
      loadingMore,
      message,
      bannerLoading: bannerState === 'loading',
      gameLoading: gameState === 'loading',
      error,
      query,
      setQuery,
      category,
      setCategory,
      providerCode,
      setProviderCode,
      loadMore: () => {
        void loadMore();
      },
      reload: () => {
        void loadBanners();
        void loadGames();
      },
    }),
    [
      banners,
      games,
      total,
      hasMore,
      loadingMore,
      message,
      bannerState,
      gameState,
      error,
      query,
      category,
      providerCode,
      loadBanners,
      loadGames,
      loadMore,
    ],
  );
}
